import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { emailsApi, templatesApi } from '../services/api'
import { X, Upload, Mail, AlertCircle, RefreshCw, Plus, Trash2, ChevronRight, FileText, ShieldAlert } from 'lucide-react'
import SearchableSelect from './SearchableSelect'
import DateTimePicker from './DateTimePicker'
import { HIDE_TEMPLATE_SELECTOR_FLAG } from '../config/runtimeConfig'
import { inWorkspace } from '../config/opcoWorkspace'

const HIDE_TEMPLATE_SELECTOR = HIDE_TEMPLATE_SELECTOR_FLAG

// Matches the .dialog-panel[data-state="closing"] animation in index.css.
const EXIT_MS = 200

const INPUT_CLASS = 'form-input text-sm hover:border-gray-300 dark:hover:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500'

// Attachments are keyed by this rather than their index, so removing one
// doesn't remount (and re-animate) every row after it.
let attachmentSeq = 0

// A shared default: a fresh `{}` per render would re-fire the effect that
// copies initialFormData into the form, which sets state, which re-renders —
// an endless loop whenever the caller omits the prop.
const NO_INITIAL_FORM_DATA = Object.freeze({})

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function IngestEmailModal({ onClose, onSuccess, initialTemplateId = '', initialFormData = NO_INITIAL_FORM_DATA }) {
  const [formData, setFormData] = useState({
    subject: initialFormData.subject || '',
    sender: initialFormData.sender || '',
    recipients: initialFormData.recipients || '',
    body: initialFormData.body || '',
    received_at: initialFormData.received_at || '',
    message_id: '',
    template_id: initialTemplateId ? String(initialTemplateId) : '', // Changed from request_type to template_id
    auto_analyze: true,
  })
  const [attachments, setAttachments] = useState([])
  const [templates, setTemplates] = useState([]) // Templates with analyzers
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showTemplateFields, setShowTemplateFields] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [userFieldValues, setUserFieldValues] = useState({})
  const [duplicates, setDuplicates] = useState({}) // { filename: { request_id, created_at, status, field_count, request_url } }
  const [closing, setClosing] = useState(false)
  const [scrollEdges, setScrollEdges] = useState({ top: false, bottom: false })
  const closeTimer = useRef(null)
  const subjectRef = useRef(null)
  const bodyRef = useRef(null)

  // Play the exit transition, then hand back to the parent, which unmounts us.
  const dismiss = useCallback((after) => {
    if (closeTimer.current) return
    setClosing(true)
    closeTimer.current = setTimeout(after, prefersReducedMotion() ? 0 : EXIT_MS)
  }, [])

  const requestClose = useCallback(() => {
    if (!loading) dismiss(onClose)
  }, [loading, dismiss, onClose])

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  // Lock the page behind the dialog, focus the first field once the panel has
  // settled, and give focus back to whatever opened us when we go.
  useEffect(() => {
    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusTimer = setTimeout(() => subjectRef.current?.focus({ preventScroll: true }), 160)
    return () => {
      clearTimeout(focusTimer)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus?.({ preventScroll: true })
    }
  }, [])

  // Header and footer pick up a soft shadow only while content scrolls under them.
  const updateScrollEdges = useCallback(() => {
    const el = bodyRef.current
    if (!el) return
    const top = el.scrollTop > 2
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2
    setScrollEdges(prev => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }))
  }, [])

  useEffect(() => {
    const el = bodyRef.current
    updateScrollEdges()
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(updateScrollEdges)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [updateScrollEdges])

  // Get the selected template with full field information
  const selectedTemplate = useMemo(() => {
    if (!formData.template_id) return null
    return templates.find(t => String(t.id) === String(formData.template_id)) || null
  }, [templates, formData.template_id])

  // Get user input fields (is_required = true) from selected template
  const userInputFields = useMemo(() => {
    if (!selectedTemplate || !selectedTemplate.fields) return []
    return selectedTemplate.fields.filter(f => f.is_required && f.is_active !== false)
  }, [selectedTemplate])

  // Reset user field values when template changes
  useEffect(() => {
    setUserFieldValues({})
    setDuplicates({})
  }, [formData.template_id])

  useEffect(() => {
    if (!initialTemplateId) return
    setFormData(prev => ({ ...prev, template_id: String(initialTemplateId) }))
  }, [initialTemplateId])

  useEffect(() => {
    if (!initialFormData || typeof initialFormData !== 'object') return
    setFormData(prev => ({
      ...prev,
      subject: initialFormData.subject || '',
      sender: initialFormData.sender || '',
      recipients: initialFormData.recipients || '',
      body: initialFormData.body || '',
      received_at: initialFormData.received_at || '',
    }))
  }, [initialFormData])

  // Check for duplicates when attachments or template change
  useEffect(() => {
    if (!attachments.length || !formData.template_id) {
      setDuplicates({})
      return
    }
    const checkDups = async () => {
      try {
        const files = attachments.map(a => ({
          filename: a.filename,
          content_base64: a.content_base64
        }))
        const res = await emailsApi.checkDuplicates({ files, template_id: formData.template_id })
        const data = res.data || res
        setDuplicates(data.duplicates || {})
      } catch (err) {
        console.error('Duplicate check failed:', err)
        setDuplicates({})
      }
    }
    checkDups()
  }, [attachments, formData.template_id])

  // Determine if submission is blocked by duplicates
  const duplicateFiles = Object.keys(duplicates)
  const hasDuplicates = duplicateFiles.length > 0
  const duplicatesBlocked = hasDuplicates && selectedTemplate && selectedTemplate.allow_reprocessing === false
  
  // Fetch templates with analyzers on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true)
        const response = await templatesApi.list({ has_analyzer: true, include_fields: true })
        const data = response.data || response
        setTemplates(data.templates || [])
      } catch (err) {
        console.error('Error fetching templates:', err)
      } finally {
        setLoadingTemplates(false)
      }
    }
    fetchTemplates()
  }, [])
  
  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [requestClose])
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }
  
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || [])
    const rejected = []
    
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        rejected.push(file.name)
        continue
      }
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result?.split(',')[1] || ''
        setAttachments(prev => [...prev, {
          filename: file.name,
          content_type: file.type,
          content_base64: base64,
          size: file.size,
          _key: ++attachmentSeq,
        }])
      }
      reader.readAsDataURL(file)
    }
    
    if (rejected.length > 0) {
      setError(`Only PDF files are allowed. Rejected: ${rejected.join(', ')}`)
    }
    
    e.target.value = ''
  }
  
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }
  
  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files || [])
    processFiles(files)
  }
  
  const processFiles = (files) => {
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.pdf')) continue
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result?.split(',')[1] || ''
        setAttachments(prev => [...prev, {
          filename: file.name,
          content_type: file.type,
          content_base64: base64,
          size: file.size,
          _key: ++attachmentSeq,
        }])
      }
      reader.readAsDataURL(file)
    }
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    
    if (!formData.subject || !formData.sender) {
      setError('Subject and sender are required')
      return
    }

    if (!formData.template_id) {
      setError('Please select a template')
      return
    }

    if (!attachments || attachments.length === 0) {
      setError('Please attach at least one file')
      return
    }

    if (duplicatesBlocked) {
      setError('Submission blocked - duplicate document(s) detected and reprocessing is disabled for this template')
      return
    }
    
    try {
      setLoading(true)
      
      const payload = {
        ...formData,
        recipients: formData.recipients || '',
        attachments: attachments.map(({ _key, ...att }) => att),
        user_field_values: Object.keys(userFieldValues).length > 0 ? userFieldValues : undefined,
      }
      
      await emailsApi.ingest(payload)
      // Stay in the loading state while the dialog animates out.
      dismiss(onSuccess)
    } catch (err) {
      console.error('Error ingesting email:', err)
      setError(err.response?.data?.error || 'Failed to ingest email')
      setLoading(false)
    }
  }
  
  const state = closing ? 'closing' : 'open'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        data-state={state}
        aria-hidden="true"
        className="dialog-scrim fixed inset-0 bg-gray-950/45 backdrop-blur-[3px] dark:bg-black/60"
        onClick={requestClose}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ingest-email-title"
        aria-describedby="ingest-email-description"
        data-state={state}
        className="dialog-panel relative flex w-full max-w-2xl max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_28px_70px_-20px_rgba(15,23,42,0.45)] ring-1 ring-gray-900/[0.07] dark:bg-gray-900 dark:ring-white/10"
      >
        {/* Header */}
        <div
          className={`relative z-10 flex flex-shrink-0 items-start justify-between gap-3 border-b px-6 pt-6 pb-4 transition-[border-color,box-shadow] duration-300 ${
            scrollEdges.top
              ? 'border-gray-200 shadow-[0_8px_16px_-14px_rgba(15,23,42,0.35)] dark:border-gray-800'
              : 'border-gray-100 dark:border-gray-800/70'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="dialog-icon flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10 shadow-navy-900/20">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 id="ingest-email-title" className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">Ingest Email</h2>
              <p id="ingest-email-description" className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Create a request from an email and its attachments.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={loading}
            aria-label="Close modal"
            className="group flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 transition-colors hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 dark:hover:bg-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-enabled:group-hover:rotate-90" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div ref={bodyRef} onScroll={updateScrollEdges} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="px-6 py-5 space-y-4">
              {error && (
                <div
                  key={error}
                  role="alert"
                  className="dialog-alert flex items-center rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/30"
                >
                  <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0" />
                  <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
                </div>
              )}

              {/* Subject */}
              <div className="dialog-item" style={{ '--i': 0 }}>
                <label className="form-label" htmlFor="ingest-subject">Subject <span className="text-red-500">*</span></label>
                <input
                  ref={subjectRef}
                  id="ingest-subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="Email subject"
                  required
                />
              </div>

              {/* Sender */}
              <div className="dialog-item" style={{ '--i': 1 }}>
                <label className="form-label" htmlFor="ingest-sender">Sender <span className="text-red-500">*</span></label>
                <input
                  id="ingest-sender"
                  type="email"
                  name="sender"
                  value={formData.sender}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="sender@example.com"
                  required
                />
              </div>

              {/* Recipients */}
              <div className="dialog-item" style={{ '--i': 2 }}>
                <label className="form-label" htmlFor="ingest-recipients">Recipients</label>
                <input
                  id="ingest-recipients"
                  type="text"
                  name="recipients"
                  value={formData.recipients}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="recipient1@example.com, recipient2@example.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Comma-separated email addresses</p>
              </div>

              {/* Body */}
              <div className="dialog-item" style={{ '--i': 3 }}>
                <label className="form-label" htmlFor="ingest-body">Body</label>
                <textarea
                  id="ingest-body"
                  name="body"
                  value={formData.body}
                  onChange={handleChange}
                  className={`${INPUT_CLASS} min-h-[104px] resize-y`}
                  rows={4}
                  placeholder="Email body content..."
                />
              </div>

              <div className={`dialog-item grid ${HIDE_TEMPLATE_SELECTOR ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-4`} style={{ '--i': 4 }}>
                {/* Template Selection - hidden when VITE_HIDE_TEMPLATE_SELECTOR=true */}
                {!HIDE_TEMPLATE_SELECTOR && (
                <div>
                  <label className="form-label">Select Template <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.template_id ? String(formData.template_id) : ''}
                    onChange={(v) => setFormData(prev => ({ ...prev, template_id: v }))}
                    options={templates.map(t => ({ value: String(t.id), label: t.name }))}
                    disabled={loadingTemplates}
                    allLabel={loadingTemplates ? 'Loading templates…' : (templates.length === 0 ? 'No templates available' : 'Select a template')}
                    searchPlaceholder="Search templates…"
                    triggerClassName="w-full px-3.5 py-2.5 shadow-sm"
                  />
                  {selectedTemplate && selectedTemplate.description && (
                    <p className="animate-panel-in mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {selectedTemplate.description}
                    </p>
                  )}
                </div>
                )}

                {/* Received At */}
                <div>
                  <label className="form-label">Received At</label>
                  <DateTimePicker
                    value={formData.received_at}
                    onChange={(v) => setFormData(prev => ({ ...prev, received_at: v }))}
                    ariaLabel="Received at"
                  />
                </div>
              </div>

              {/* Attachments */}
              <div className="dialog-item" style={{ '--i': 5 }}>
                <label className="form-label">Attachments <span className="text-red-500">*</span></label>
                <div
                  className={`group rounded-xl border-2 border-dashed p-6 transition-all duration-200 ease-out ${
                    isDragOver
                      ? 'scale-[1.01] border-navy-500 bg-navy-50/80 ring-4 ring-navy-500/10 dark:bg-navy-900/20'
                      : 'border-gray-300 hover:border-navy-400 hover:bg-gray-50/70 dark:border-gray-700 dark:hover:border-navy-600 dark:hover:bg-gray-800/40'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    // Moving onto a child fires dragleave on the parent; only
                    // reset once the pointer has really left the drop zone.
                    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false)
                  }}
                  onDrop={handleDrop}
                >
                  {attachments.length === 0 ? (
                    <label className="flex flex-col items-center cursor-pointer py-2">
                      <div className={`flex h-11 w-11 items-center justify-center rounded-xl mb-2 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                        isDragOver
                          ? 'scale-110 bg-navy-100 text-navy-600 dark:bg-navy-800/60 dark:text-navy-200'
                          : 'bg-gray-100 text-gray-400 group-hover:-translate-y-0.5 group-hover:bg-navy-50 group-hover:text-navy-500 dark:bg-gray-800 dark:text-gray-500 dark:group-hover:bg-navy-900/30 dark:group-hover:text-navy-300'
                      }`}>
                        <Upload className={`h-5 w-5 ${isDragOver ? 'dialog-drop-bob' : ''}`} />
                      </div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        PDF only
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileChange}
                        multiple
                        accept=".pdf"
                      />
                    </label>
                  ) : (
                    <div className="space-y-2">
                      {attachments.map((att, index) => (
                        <div
                          key={att._key}
                          className="animate-panel-in flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2.5 transition-colors hover:border-gray-200 dark:border-gray-700/60 dark:bg-gray-800 dark:hover:border-gray-600"
                        >
                          <div className="flex items-center min-w-0">
                            <span className="mr-2.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-white text-navy-500 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-navy-400 dark:ring-gray-700">
                              <FileText className="h-3.5 w-3.5" />
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{att.filename}</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0">
                              ({formatFileSize(att.size)})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            aria-label={`Remove ${att.filename}`}
                            className="flex-shrink-0 rounded-md p-1 text-gray-400 transition-all hover:bg-red-50 hover:text-red-600 active:scale-90 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <label className="group/add flex items-center justify-center py-2 text-sm font-medium text-navy-600 dark:text-navy-400 cursor-pointer hover:text-navy-700 dark:hover:text-navy-300 transition-colors">
                        <Plus className="h-4 w-4 mr-1 transition-transform duration-300 group-hover/add:rotate-90" />
                        Add more files
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileChange}
                          multiple
                          accept=".pdf"
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Template Fields Preview - hidden when template selector is hidden */}
              {!HIDE_TEMPLATE_SELECTOR && selectedTemplate && selectedTemplate.fields && selectedTemplate.fields.length > 0 && (
                <div className="animate-panel-in border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowTemplateFields(!showTemplateFields)}
                    aria-expanded={showTemplateFields}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-navy-600 dark:text-navy-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Fields to Extract ({selectedTemplate.field_count || selectedTemplate.fields.length})
                      </span>
                    </div>
                    <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${showTemplateFields ? 'rotate-90' : ''}`} />
                  </button>

                  {/* Height animates via grid rows 0fr -> 1fr */}
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${showTemplateFields ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                    aria-hidden={!showTemplateFields}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className={`px-4 py-3 max-h-48 overflow-y-auto transition-opacity duration-300 ${showTemplateFields ? 'opacity-100' : 'opacity-0'}`}>
                        {selectedTemplate.fields_by_category ? (
                          // Show fields grouped by category
                          Object.entries(selectedTemplate.fields_by_category).map(([category, fields]) => (
                            <div key={category} className="mb-3 last:mb-0">
                              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {selectedTemplate.category_display_names?.[category] || category}
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {fields.map(field => (
                                  <span
                                    key={field.id}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                    title={field.display_name || field.field_name}
                                  >
                                    {field.display_name || field.field_name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))
                        ) : (
                          // Show flat list of fields
                          <div className="flex flex-wrap gap-1">
                            {selectedTemplate.fields.map(field => (
                              <span
                                key={field.id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                title={field.display_name || field.field_name}
                              >
                                {field.display_name || field.field_name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Fields (User Input Fields) */}
              {userInputFields.length > 0 && (
                <div className="animate-panel-in border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Add Custom Fields
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      ({userInputFields.length} {userInputFields.length === 1 ? 'field' : 'fields'})
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {userInputFields.map(field => (
                      <div key={field.id} className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400" htmlFor={`uif-${field.id}`}>
                          {field.display_name || field.field_name}
                        </label>
                        <input
                          type="text"
                          id={`uif-${field.id}`}
                          value={userFieldValues[field.field_name] || ''}
                          onChange={(e) => setUserFieldValues(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                          placeholder={field.description || field.display_name || field.field_name}
                          className={INPUT_CLASS}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Already Processed Section */}
              {hasDuplicates && (
                <div className={`animate-panel-in border rounded-lg overflow-hidden ${duplicatesBlocked ? 'border-brand-orange-dark dark:border-brand-orange-mid' : 'border-brand-orange-mid dark:border-brand-orange-bright'}`}>
                  <div className={`flex items-center gap-2 px-4 py-3`}>
                    <ShieldAlert className={`h-4 w-4 ${duplicatesBlocked ? 'text-brand-orange-dark dark:text-brand-orange-mid' : 'text-brand-orange-mid dark:text-brand-orange-bright'}`} />
                    <span className={`text-sm font-medium ${duplicatesBlocked ? 'text-brand-orange-dark dark:text-brand-orange-mid' : 'text-brand-orange-mid dark:text-brand-orange-bright'}`}>
                      Already processed with this Template
                    </span>
                  </div>
                <div className="p-4 space-y-2">
                  {duplicateFiles.map(filename => {
                    const dup = duplicates[filename]
                    const isApproved = (dup.status || '').toLowerCase().includes('approved')
                    const dateLabel = isApproved ? 'Approved date' : 'Last run'
                    return (
                      <div key={filename} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{filename}</p>
                          {dup.in_other_opco ? (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Already processed in another OpCo</p>
                          ) : (
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span><strong>{dateLabel}:</strong> {dup.created_at ? new Date(dup.created_at).toLocaleDateString() : '-'}</span>
                            <span><strong>Request ID:</strong> #{dup.request_id}</span>
                            <span><strong>Status:</strong> <span className="text-brand-aqua-dark dark:text-brand-aqua-bright">✓ {dup.status}</span></span>
                          </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {dup.request_url && (
                            <a href={inWorkspace(dup.request_url)} target="_blank" rel="noopener noreferrer"
                              className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-600 text-brand-blue-bright dark:text-brand-blue-bright hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                              View Results →
                            </a>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {duplicatesBlocked && (
                  <div className="px-4 pb-3">
                    <p className="text-xs font-medium text-brand-orange-dark dark:text-brand-orange-mid">
                      🚫 Reprocessing is disabled for this template. Submission blocked.
                    </p>
                  </div>
                )}
                {!duplicatesBlocked && hasDuplicates && (
                  <div className="px-4 pb-3">
                    <p className="text-xs text-brand-orange-mid dark:text-brand-orange-bright">
                      ⚠️ Duplicate detected - reprocessing is allowed for this template.
                    </p>
                  </div>
                )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className={`relative z-10 flex flex-shrink-0 justify-end gap-2.5 border-t bg-white px-6 py-4 transition-[border-color,box-shadow] duration-300 dark:bg-gray-900 ${
              scrollEdges.bottom
                ? 'border-gray-200 shadow-[0_-8px_16px_-14px_rgba(15,23,42,0.35)] dark:border-gray-800'
                : 'border-gray-100 dark:border-gray-800/70'
            }`}
          >
            <button
              type="button"
              onClick={requestClose}
              disabled={loading}
              className="inline-flex items-center h-9 px-4 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 dark:hover:bg-gray-800 dark:hover:border-gray-600 enabled:active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || duplicatesBlocked}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-navy-800 text-white text-sm font-semibold shadow-sm transition-all duration-200 hover:bg-navy-900 enabled:hover:-translate-y-0.5 enabled:hover:shadow-md enabled:hover:shadow-navy-900/20 enabled:active:translate-y-0 enabled:active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Ingesting…
                </>
              ) : hasDuplicates && !duplicatesBlocked ? (
                'Reprocess'
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Ingest Email
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export default IngestEmailModal
